import { QConfigStatus } from '@exam-management/graphql';
import { type TableCellProps, Text } from '@shared/components';
import { useHasPermission } from '@shared/permissions';
import { TableHeader, type TableHeaderRow } from '@shared/table-generator';

import { EDIT_QCONFIG_REQUIRED_PERMISSIONS } from '../../constants';

interface QConfigStructureTableHeaderProps {
  status: QConfigStatus;
}

function getHeadersByStatusAndPermission(
  status: QConfigStatus,
  hasPermission: boolean,
): TableHeaderRow[] {
  const isDraftQConfig = status === QConfigStatus.Draft;

  const moreActionColumn: TableCellProps | undefined = {
    children: '',
    align: 'center' as const,
    rowSpan: 2,
    width: 48,
  };

  return [
    {
      children: [
        {
          id: 'subject',
          children: 'วิชา',
          align: 'center',
          rowSpan: 2,
        },

        {
          id: 'refCode',
          children: 'QSource',
          align: 'center',
          rowSpan: 2,
          width: 112,
        },
        {
          id: 'totalQuestion',
          children: 'จำนวนข้อสอบ',
          align: 'center',
          rowSpan: 2,
          width: 80,
        },
        {
          id: 'scorePerQuestion',
          children: 'คะแนน',
          align: 'center',
          rowSpan: 2,
          width: 80,
        },
        {
          id: 'questionType',
          children: (
            <>
              QType<Text variant='body2'>(S/IS)</Text>
            </>
          ),
          align: 'center',
          rowSpan: 2,
          width: 100,
        },
        {
          id: 'calculated',
          children: 'คำนวณ',
          align: 'center',
          colSpan: 3,
          sx: {
            py: (theme) => theme.spacing(2),
          },
        },
        {
          id: 'not-calculated',
          children: 'ไม่คำนวณ',
          align: 'center',
          colSpan: 3,
          sx: {
            py: (theme) => theme.spacing(2),
          },
        },
        ...(isDraftQConfig && hasPermission ? [moreActionColumn] : []),
      ],
    },
    {
      children: [
        {
          children: 'ง่าย',
          align: 'center',
          width: 60,
          sx: {
            py: (theme) => theme.spacing(2),
          },
        },
        {
          children: 'กลาง',
          align: 'center',
          width: 60,
          sx: {
            py: (theme) => theme.spacing(2),
          },
        },
        {
          children: 'ยาก',
          align: 'center',
          width: 60,
          sx: {
            py: (theme) => theme.spacing(2),
          },
        },
        {
          children: 'ง่าย',
          align: 'center',
          width: 60,
          sx: {
            py: (theme) => theme.spacing(2),
          },
        },
        {
          children: 'กลาง',
          align: 'center',
          width: 60,
          sx: {
            py: (theme) => theme.spacing(2),
          },
        },
        {
          children: 'ยาก',
          align: 'center',
          width: 60,
          sx: {
            py: (theme) => theme.spacing(2),
            borderRight: isDraftQConfig ? '1px solid' : 'none',
          },
        },
      ],
    },
  ];
}
export default function QConfigStructureTableHeader(
  props: Readonly<QConfigStructureTableHeaderProps>,
) {
  const { status } = props;
  const hasPermission = useHasPermission(EDIT_QCONFIG_REQUIRED_PERMISSIONS);

  const columns = getHeadersByStatusAndPermission(status, hasPermission);
  return (
    <TableHeader
      columns={columns}
      slotProps={{
        tableHeader: {
          sx: (theme) => ({
            bgcolor: theme.palette.common.black,
            borderColor: theme.palette.grey[300],
            border: 'none',
          }),
        },
        tableHeaderRow: {
          sx: {
            ':first-of-type': {
              'th:last-of-type': {
                borderRight: 'none',
              },
            },
            ':last-of-type > th': {
              borderTop: '1px solid',
              borderColor: (theme) => theme.palette.grey[300],
            },
          },
        },
        tableHeaderCell: {
          sx: (theme) => ({
            ...theme.typography.body1Semibold,
            color: theme.palette.common.white,
            backgroundColor: theme.palette.common.black,
            padding: theme.spacing(3),
            borderRight: '1px solid',
            borderBottom: 'none',
            borderColor: theme.palette.grey[300],
          }),
        },
      }}
    />
  );
}
